import { get, post, Delete } from "../services/api";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import { KitOverview, Plan, DeactivationCode, TopUp } from "../types/store/starlink";
import { rootState } from "../types/store/state";
import { Notify } from "quasar";
import i18n from "../i18n";
import axios, { AxiosError } from "axios";

const REBOOT_POLL_MS = 30_000;
const REBOOT_MAX_ATTEMPTS = 60;
const REBOOT_MAX_CONSECUTIVE_ERRORS = 3;

export interface StarlinkModule {
  namespaced: boolean;
  state: () => StarlinkState;
  getters: GetterTree<StarlinkState, rootState>;
  actions: ActionTree<StarlinkState, rootState>;
  mutations: MutationTree<StarlinkState>;
}

export interface StarlinkState {
  kits: KitOverview[];
  plans: Plan[];
  deactivationCodes: DeactivationCode[];
  topUps: Record<string, TopUp[]>;
}

const starlink_module: StarlinkModule = {
  namespaced: true,
  state: () => ({
    kits: [],
    plans: [],
    deactivationCodes: [],
    topUps: {},
  }),
  getters: {
    kits: (state) => {
      return state.kits;
    },
    plans: (state) => {
      return state.plans;
    },
    deactivationCodes: (state) => {
      return state.deactivationCodes;
    },
    topUps: (state) => (companyId: string) => state.topUps[companyId] ?? [],
  },
  actions: {
    async getPlans({ commit, rootGetters }) {
      return new Promise((resolve, reject) => {
        get(`tsapi/${rootGetters["company_id"]}/starlink/plans`)
          .then(({ data }) => {
            commit("updatePlans", data);
            resolve(null);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    async getDeactivationCodes({ commit }) {
      return new Promise((resolve, reject) => {
        get(`itapi/starlink/kits/deactivationcodes`)
          .then(({ data }) => {
            commit("updateDeactivationCodes", data);
            resolve(null);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    async getKits({ commit, rootGetters }) {
      return new Promise((resolve, reject) => {
        get(`itapi/${rootGetters["company_id"]}/starlink`)
          .then(({ data }) => {
            commit("updateKits", data.kits);
            resolve(null);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    async updateKitName({ commit, rootGetters }, kit: any) {
      const { kitId, kitName } = kit;

      try {
        commit("updateKit", {
          kitId,
          isUpdating: true,
        });
        await post(`itapi/${rootGetters["company_id"]}/starlink/kits/${kitId}/name`, {
          kitName,
        });
        commit("updateKit", {
          kitId,
          nickname: kitName,
        });
      } catch (error) {
        console.error("Error updating kit name:", error);
        throw error;
      } finally {
        commit("updateChangeLog", {
          kitId: kitId,
          changeLog: {
            timestamp: new Date().toISOString(),
            username: rootGetters["username"],
            action: "Name Change",
            changeType: "nameChange",
            kitId: kitId,
          },
        });
        commit("updateKit", {
          kitId,
          isUpdating: false,
        });
      }
    },
    async pollPlanChangeStatus({ commit, dispatch, rootGetters }, payload) {
      try {
        let status;
        if (payload.planChangeRecordId) {
          let searchParams = new URLSearchParams();
          searchParams.set("planChangeRecordId", payload.planChangeRecordId);
          const { data } = await get(
            `itapi/${rootGetters["company_id"]}/starlink/kits/${payload.kitId}/planchange?${searchParams.toString()}`
          );
          status = data.status;
        }
        if (status == "completed") {
          commit("updateKit", {
            kitId: payload.kitId,
            pricePlanId: payload.planId,
            pricePlanName: payload.planName,
            planChangedStatus: "Completed",
          });
          commit("updateChangeLog", {
            kitId: payload.kitId,
            changeLog: {
              timestamp: new Date().toISOString(),
              username: rootGetters["username"],
              action: "Plan Change",
              changeType: "plans",
              kitId: payload.kitId,
            },
          });
          Notify.create({
            message: i18n.global.t("starlink.updateMsg", { kitId: payload.kitId }),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
            classes: "bg-primary-medium-theme-color",
          });
        } else if (status == "failed") {
          commit("updateKit", {
            planChangedStatus: "Failed",
            kitId: payload.kitId,
          });
          commit("updateChangeLog", {
            kitId: payload.kitId,
            changeLog: {
              timestamp: new Date().toISOString(),
              username: rootGetters["username"],
              action: "Plan Change",
              changeType: "plans",
              kitId: payload.kitId,
            },
          });
          Notify.create({
            message: i18n.global.t("starlink.errorChangeKitStatus", { kitId: payload.kitId }),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        } else if (status === "pending") {
          setTimeout(() => {
            dispatch("pollPlanChangeStatus", payload);
          }, 3000);
        } else if (status === "canceled") {
          commit("updateKit", {
            planChangedStatus: "Canceled",
            kitId: payload.kitId,
          });
        } else {
          console.error("Error check task", status);
          commit("updateKit", {
            planChangedStatus: "Failed",
            kitId: payload.kitId,
          });
          commit("updateChangeLog", {
            kitId: payload.kitId,
            changeLog: {
              timestamp: new Date().toISOString(),
              username: rootGetters["username"],
              action: "Plan Change",
              changeType: "plans",
              kitId: payload.kitId,
            },
          });
          Notify.create({
            message: i18n.global.t("starlink.errorChangeKitStatus", { kitId: payload.kitId }),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        }
      } catch (error) {
        console.error("Error retrieving status");
      }
    },
    async pollDeactivationStatus({ commit, dispatch, rootGetters }, payload) {
      try {
        let status;
        if (payload.deactivationRecordId) {
          let searchParams = new URLSearchParams();
          searchParams.set("deactivationRecordId", payload.deactivationRecordId);
          const { data } = await get(
            `itapi/${rootGetters["company_id"]}/starlink/kits/${payload.kitId}/deactivate?${searchParams.toString()}`
          );
          status = data.status;
        }
        if (status == "completed") {
          commit("updateKit", {
            kitId: payload.kitId,
            statusCode: "Deactivated",
            operationStatus: "Completed",
          });
          commit("updateChangeLog", {
            kitId: payload.kitId,
            changeLog: {
              timestamp: new Date().toISOString(),
              username: rootGetters["username"],
              action: "Deactivate",
              kitId: payload.kitId,
            },
          });
          Notify.create({
            message: i18n.global.t("starlink.updateMsg", { kitId: payload.kitId }),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
            classes: "bg-primary-medium-theme-color",
          });
        } else if (status == "failed") {
          commit("updateKit", {
            operationStatus: "Failed",
            kitId: payload.kitId,
          });
          Notify.create({
            message: i18n.global.t("starlink.errorChangeKitStatus", { kitId: payload.kitId }),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        } else if (status === "pending") {
          setTimeout(() => {
            dispatch("pollDeactivationStatus", payload);
          }, 3000);
        } else if (status === "canceled") {
          commit("updateKit", {
            operationStatus: "Failed",
            kitId: payload.kitId,
          });
        } else {
          console.error("Error check task", status);
          commit("updateKit", {
            operationStatus: "Failed",
            kitId: payload.kitId,
          });
          commit("updateChangeLog", {
            kitId: payload.kitId,
            changeLog: {
              timestamp: new Date().toISOString(),
              username: rootGetters["username"],
              action: "Deactivate",
              kitId: payload.kitId,
            },
          });
          Notify.create({
            message: i18n.global.t("starlink.errorChangeKitStatus", { kitId: payload.kitId }),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        }
      } catch (error) {
        console.error("Error retrieving status");
      }
    },
    async pollRebootStatus(
      { commit, dispatch, rootGetters },
      payload: { kitId: string; attempts?: number; consecutiveErrors?: number }
    ) {
      const { kitId } = payload;
      const attempts = payload.attempts ?? 0;
      const consecutiveErrors = payload.consecutiveErrors ?? 0;
      const finish = (status: string) => {
        commit("updateKit", { kitId, isRebooting: false });
        commit("updateChangeLog", {
          kitId,
          changeLog: {
            timestamp: new Date().toISOString(),
            username: rootGetters["username"],
            action: "Reboot",
            changeType: "reboot",
            kitId,
          },
        });
        if (status === "completed") {
          Notify.create({
            message: i18n.global.t("starlink.rebootCompleted", { kitId }),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
            classes: "bg-primary-medium-theme-color",
          });
        } else {
          Notify.create({
            message: i18n.global.t(status === "timed_out" ? "starlink.rebootTimedOut" : "starlink.rebootFailed", {
              kitId,
            }),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        }
      };

      try {
        const { data } = await get(`itapi/starlink/${kitId}/reboot`);
        const status = data?.status;
        if (status === "submitted") {
          if (attempts >= REBOOT_MAX_ATTEMPTS) {
            finish("timed_out");
            return;
          }
          setTimeout(() => {
            dispatch("pollRebootStatus", { kitId, attempts: attempts + 1 });
          }, REBOOT_POLL_MS);
        } else if (status === "completed" || status === "timed_out") {
          finish(status);
        } else {
          if (status !== "failed") console.error("Unexpected reboot status", status);
          finish("failed");
        }
      } catch (error) {
        console.error("Error retrieving reboot status", error);
        if (consecutiveErrors + 1 >= REBOOT_MAX_CONSECUTIVE_ERRORS || attempts >= REBOOT_MAX_ATTEMPTS) {
          finish("failed");
          return;
        }
        setTimeout(() => {
          dispatch("pollRebootStatus", { kitId, attempts: attempts + 1, consecutiveErrors: consecutiveErrors + 1 });
        }, REBOOT_POLL_MS);
      }
    },
    async deactivate({ commit, dispatch, rootGetters }, payload: any) {
      const { kitId, deactivationCode, childDeactivationCode, deactivateAt } = payload;
      try {
        const url = `itapi/v2/${rootGetters["company_id"]}/starlink/kits/${kitId}/deactivate`;
        const { data } = await post(url, { deactivationCode, childDeactivationCode, deactivateAt });
        if (data.deactivateAt) {
          commit("updateKit", {
            kitId,
            scheduledDeactivation: { deactivateAt, submittedAt: "Just now", status: "scheduled" },
          });
        } else {
          commit("updateKit", {
            kitId,
            operationStatus: "Confirmed",
          });
          setTimeout(() => {
            dispatch("pollDeactivationStatus", { kitId, deactivationRecordId: data.deactivationRecordId });
          }, 3000);
        }
        Notify.create({
          message: i18n.global.t("popupDeactivate.scheduled"),
          type: "positive",
          actions: [{ label: "Dismiss", color: "black" }],
          position: "bottom-right",
        });
      } catch (e) {
        Notify.create({
          message: i18n.global.t("popupDeactivate.error"),
          type: "negative",
          actions: [{ label: "Dismiss", color: "white" }],
          position: "bottom-right",
        });
      }
    },
    async updateCurrentPlan({ commit, dispatch, rootGetters }, payload: any) {
      const { kitId, planId, planName, address } = payload;

      try {
        const url = `itapi/${rootGetters["company_id"]}/starlink/kits/${kitId}/planchange`;
        const { data } = await post(url, { planId, planName, address });

        if (data.changeTime == "Immediate") {
          commit("updateKit", {
            planChangedStatus: "Pending",
            kitId,
          });
          setTimeout(() => {
            dispatch("pollPlanChangeStatus", { kitId, planName, planId, planChangeRecordId: data.planChangeRecordId });
          }, 3000);
        } else if (data.changeTime == "EndOfMonth") {
          commit("updateKit", {
            kitId,
            planChangedStatus: "Scheduled",
            scheduledPlanChange: { planId, planName, submittedAt: "Just now" },
          });
          Notify.create({
            message: i18n.global.t("popupPlanChange.scheduled"),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
          });
        } else {
          Notify.create({
            message: i18n.global.t("popupPlanChange.error"),
            type: "negative",
            actions: [{ label: "Dismiss", color: "white" }],
            position: "bottom-right",
          });
        }
      } catch (e) {
        const errors = e as Error | AxiosError;
        if (!axios.isAxiosError(errors)) {
          const message = errors.message;
          throw new Error(message);
        } else {
          throw new Error(errors?.message || errors?.response?.data?.message);
        }
      }
    },
    async deletePlanChange({ commit, rootGetters }, kit) {
      try {
        const { data } = await Delete(`itapi/${rootGetters["company_id"]}/starlink/kits/${kit.kitId}/planchange`);
        if (data.status == "Completed") {
          commit("updateKit", {
            kitId: kit.kitId,
            planChangedStatus: "Canceled",
            scheduledPlanChange: null,
          });
          Notify.create({
            message: i18n.global.t("popupPlanChange.deletePlanChangeRequestSuccess"),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
          });
        }
      } catch (e) {
        Notify.create({
          message: i18n.global.t("popupPlanChange.deletePlanChangeRequestError"),
          type: "negative",
          actions: [{ label: "Dismiss", color: "white" }],
          position: "bottom-right",
        });
      }
    },
    async deleteDeactivation({ commit, rootGetters }, kit) {
      try {
        const { data } = await Delete(`itapi/${rootGetters["company_id"]}/starlink/kits/${kit.kitId}/deactivate`);
        if (data.status == "Completed") {
          commit("updateKit", {
            kitId: kit.kitId,
            scheduledDeactivation: null,
          });
          Notify.create({
            message: i18n.global.t("popupDeactivate.deleteDeactivationRequestSuccess"),
            type: "positive",
            actions: [{ label: "Dismiss", color: "black" }],
            position: "bottom-right",
          });
        }
      } catch (e) {
        Notify.create({
          message: i18n.global.t("popupDeactivate.deleteDeactivationRequestError"),
          type: "negative",
          actions: [{ label: "Dismiss", color: "white" }],
          position: "bottom-right",
        });
      }
    },
    async getTopUps({ commit }, companyId: string) {
      try {
        const { data } = await get(`tsapi/${companyId}/starlink/databoosters`);
        commit("updateTopUps", { companyId, data });
      } catch (err) {
        console.log(err);
      }
    },
  },
  mutations: {
    updateKit: (state, data) => {
      const kitId = data?.kitId;
      const kit = state.kits.findIndex((kit) => kit.kitId == kitId);

      const newKitData = { ...state.kits[kit], ...data };
      state.kits[kit] = newKitData;
    },
    updateChangeLog: (state, data) => {
      const kitId = data?.kitId;
      const kit = state.kits.findIndex((kit) => kit.kitId == kitId);
      state.kits[kit]?.changeLog?.unshift(data.changeLog);
    },
    updateKits: (state, data) => {
      state.kits = data;
    },
    updatePlans: (state, data) => {
      state.plans = data;
    },
    updateDeactivationCodes: (state, data) => {
      state.deactivationCodes = data;
    },
    updateTopUps: (state, { companyId, data }) => {
      state.topUps = { ...state.topUps, [companyId]: data };
    },
  },
};

export default starlink_module;
